import { Component } from "@theme/component";
import {
  fetchConfig,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import { ThemeEvents, QuantitySelectorUpdateEvent } from "@theme/events";
import { cartPerformance } from "@theme/performance";
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents
} from "@shopify/events";
export class CartItemsComponent extends createViewEventElement(Component) {
  /*
   * Nuaje (24/09/2026) : file des mises à jour du panier.
   *
   * Horizon envoyait chaque changement aussitôt, en désignant la ligne par sa
   * POSITION (`line`), avec un seul délai de 300 ms pour toutes les lignes.
   * Relevé en test :
   *  - « + » sur deux lignes à moins de 300 ms : seul le second partait ;
   *  - deux appuis sur la corbeille (clavier, double tap) : deux produits
   *    supprimés — le second appui visait la ligne qui avait pris la place ;
   *  - une quantité tapée puis une suppression : la quantité se perdait ;
   *  - réponses appliquées dans leur ordre d'arrivée, pas d'envoi ;
   *  - échec réseau : rien n'était remis d'aplomb (ligne retirée à l'écran,
   *    toujours au panier).
   * Désormais : une ligne est désignée par sa clé (`id` = item.key), les
   * changements en attente sont regroupés par ligne, les requêtes partent une
   * à une dans l'ordre, et un échec redemande le tiroir au serveur.
   *
   * Remises « Buy 2, Get 2 Free » : Shopify découpe et RENOMME les lignes dès
   * que l'offre s'applique ou tombe. Un changement en file peut donc viser
   * une clé qui n'existe plus au moment de partir. On retombe alors sur la
   * taille (variante) : l'écart voulu (+1, −2…) est appliqué à une ligne de
   * la même taille.
   */
  /**
   * @typedef {{ variantId?: string, from: number, quantity: number }} PendingChange
   * @type {Map<string, PendingChange>} Changements en attente, par clé de ligne.
   */
  #pendingQuantities = /* @__PURE__ */ new Map();
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #flushTimer;
  /** @type {Promise<void>} */
  #queue = Promise.resolve();
  #inFlight = 0;
  #needsRefresh = false;
  /** Nombre d'échecs de mise à jour depuis le chargement (voir #onSubmit). */
  #failures = 0;
  /** @type {Set<string>} Lignes dont la suppression est déjà partie. */
  #removingKeys = /* @__PURE__ */ new Set();
  /**
   * Lignes connues du serveur (clé → taille, quantité), d'après la dernière
   * réponse, ou relevées dans le tiroir avant toute retouche optimiste.
   * @type {Map<string, { variantId?: string, quantity: number }> | null}
   */
  #serverItems = null;
  /**
   * Après une suppression au clavier : la ligne (clé) qui doit garder le
   * focus une fois le tiroir re-rendu ; null = l'état vide.
   * @type {{ key: string | null } | null}
   */
  #focusIntent = null;
  /** Ligne dont la mise à jour a échoué : le message s'y affiche après recalage. */
  /** @type {string | null} */
  #failedKey = null;
  /** @returns {Map<string, { variantId?: string, quantity: number }>} */
  #ensureServerItems() {
    if (!this.#serverItems) {
      this.#serverItems = /* @__PURE__ */ new Map();
      for (const row of this.refs.cartItemRows ?? []) {
        if (!row.isConnected) continue;
        const info = this.#rowInfo(row);
        if (info.key) this.#serverItems.set(info.key, { variantId: info.variantId, quantity: info.quantity });
      }
    }
    return this.#serverItems;
  }
  /** @param {Event} event */
  #onQuantitySelectorUpdate = (event) => {
    if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
  };
  /*
   * Entrée (ou « OK » du clavier mobile) dans un champ de quantité : le champ
   * est dans #cart-form, dont le bouton par défaut est « Secure Checkout ».
   * Sans ceci, la touche envoyait le client au paiement. On valide la saisie
   * comme une sortie du champ, et rien d'autre.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== "Enter" || event.isComposing) return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches("input[data-cart-line]")) return;
    event.preventDefault();
    target.blur();
  };
  /*
   * « Secure Checkout » pendant une mise à jour : le formulaire du panier
   * envoie les quantités affichées, ligne par ligne SELON LEUR POSITION. Si une
   * requête est encore en route (ligne en cours de retrait, lignes renommées
   * par une remise), ces positions ne correspondent plus au serveur. On
   * attend la fin de la file, puis on renvoie le formulaire, à jour.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== "cart-form") return;
    if (this.#inFlight === 0 && this.#pendingQuantities.size === 0) return;
    event.preventDefault();
    const submitterId = event.submitter?.id;
    const failuresBefore = this.#failures;
    this.#flushQuantities();
    this.#queue.then(() => {
      if (this.#failures !== failuresBefore) return;
      const liveForm = document.getElementById("cart-form");
      if (!(liveForm instanceof HTMLFormElement)) return;
      const submitter = submitterId ? document.getElementById(submitterId) : null;
      liveForm.requestSubmit(submitter instanceof HTMLButtonElement ? submitter : void 0);
    }).catch(() => {
    });
  };
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }
  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise?.then(({ detail }) => {
      const sectionsHtml = detail?.sections?.[this.sectionId];
      if (sectionsHtml) {
        morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? "hydration" : "full" });
        this.#updateCartQuantitySelectorButtonStates();
      } else if (external) {
        sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? "hydration" : "full"
        });
      }
    }).catch((error) => {
      if (error?.name !== "AbortError") console.warn("[cart-items] Event promise rejected:", error);
    });
  };
  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise?.then(({ detail }) => {
      const sections = (
        /** @type {Record<string, string> | undefined} */
        detail?.sections
      );
      const sectionsHtml = sections?.[this.sectionId];
      if (sectionsHtml) {
        morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? "hydration" : "full" });
      } else {
        sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? "hydration" : "full"
        });
      }
    }).catch((error) => {
      if (error?.name !== "AbortError") console.warn("[cart-items] Event promise rejected:", error);
    });
  };
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.addEventListener("keydown", this.#onKeyDown);
    this.addEventListener("submit", this.#onSubmit);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.removeEventListener("keydown", this.#onKeyDown);
    this.removeEventListener("submit", this.#onSubmit);
  }
  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * Les changements sont regroupés par ligne et envoyés 300 ms après le
   * dernier : plusieurs « + » rapides ne font qu'une requête, et une autre
   * ligne touchée entre-temps n'annule plus la première.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;
    const row = this.#rowFor(event.target, line);
    const key = row?.dataset.key;
    if (!row || !key || this.#removingKeys.has(key)) return;
    if (quantity === 0) {
      return this.#removeRow(row);
    }
    const info = this.#rowInfo(row);
    const previous = this.#pendingQuantities.get(key);
    this.#pendingQuantities.set(key, { variantId: info.variantId, from: previous?.from ?? info.quantity, quantity });
    row.querySelector("text-component")?.shimmer();
    clearTimeout(this.#flushTimer);
    this.#flushTimer = setTimeout(() => this.#flushQuantities(), 300);
  }
  /** Envoie tout de suite les changements de quantité en attente. */
  #flushQuantities() {
    clearTimeout(this.#flushTimer);
    const pending = [...this.#pendingQuantities];
    this.#pendingQuantities.clear();
    for (const [key, change] of pending) {
      this.updateQuantity({ key, ...change, action: "change" });
    }
  }
  /**
   * @param {HTMLTableRowElement} row
   * @returns {{ key: string | undefined, variantId: string | undefined, quantity: number }}
   */
  #rowInfo(row) {
    const input = row.querySelector("input[data-cart-line]");
    const quantity = input instanceof HTMLInputElement ? parseInt(input.defaultValue, 10) || 0 : 0;
    const variantId = (
      /** @type {HTMLElement | null} */
      row.querySelector("[data-variant-id]")?.dataset.variantId
    );
    return { key: row.dataset.key, variantId, quantity };
  }
  /**
   * La ligne visée : celle qui contient l'élément touché, à défaut la
   * position transmise par le balisage.
   * @param {EventTarget | null | undefined} target
   * @param {number | string} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #rowFor(target, line) {
    const fromTarget = target instanceof Element ? target.closest("tr[data-key]") : null;
    if (fromTarget instanceof HTMLTableRowElement && this.contains(fromTarget)) return fromTarget;
    return this.refs.cartItemRows?.[Number(line) - 1];
  }
  /**
   * Handles the line item removal (corbeille).
   * @param {number} line - The line item index.
   * @param {Event} [event] - The click event.
   */
  onLineItemRemove(line, event) {
    const row = this.#rowFor(event?.target, line);
    if (row) this.#removeRow(row);
  }
  /**
   * @param {HTMLTableRowElement} cartItemRowToRemove
   */
  #removeRow(cartItemRowToRemove) {
    const key = cartItemRowToRemove.dataset.key;
    if (!key || this.#removingKeys.has(key) || cartItemRowToRemove.classList.contains("removing")) return;
    this.#removingKeys.add(key);
    const info = this.#rowInfo(cartItemRowToRemove);
    const from = this.#pendingQuantities.get(key)?.from ?? info.quantity;
    this.#pendingQuantities.delete(key);
    this.#flushQuantities();
    this.updateQuantity({
      key,
      variantId: info.variantId,
      from,
      quantity: 0,
      action: "clear"
    });
    const rows = this.refs.cartItemRows ?? [];
    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...rows.filter((row) => row.dataset.parentKey === key)
    ];
    const hadFocus = rowsToRemove.some((row) => row.contains(document.activeElement));
    const remaining = rows.filter((row) => !rowsToRemove.includes(row) && !row.classList.contains("removing"));
    const isEmptyCart = remaining.length === 0;
    const template = document.getElementById("empty-cart-template");
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      startViewTransition(() => {
        document.getElementById("cart-drawer-heading")?.remove();
        this.replaceChildren(clone);
        if (hadFocus) this.#focusAfterRemoval(null);
      }, [this.isDrawer ? "empty-cart-drawer" : "empty-cart-page"]);
      return;
    }
    if (hadFocus) {
      const index = rows.indexOf(cartItemRowToRemove);
      const next = remaining.find((row) => rows.indexOf(row) > index) ?? remaining[remaining.length - 1];
      this.#focusAfterRemoval(next ?? null);
    }
    rowsToRemove.forEach((row) => {
      const remove = () => {
        if (row.classList.contains("removing")) row.remove();
      };
      if (prefersReducedMotion()) return row.remove();
      row.style.setProperty("--row-height", `${row.clientHeight}px`);
      row.classList.add("removing");
      onAnimationEnd(row, remove);
    });
  }
  /**
   * @param {HTMLTableRowElement | null} row - Ligne qui reçoit le focus ; null = état vide.
   */
  #focusAfterRemoval(row) {
    this.#focusIntent = { key: row?.dataset.key ?? null };
    this.#applyFocusIntent();
  }
  /*
   * Focus clavier pendant un re-rendu (24/09/2026) : quand une remise renomme
   * les lignes, le tiroir est reconstruit et le bouton ou le champ qui avait
   * le focus disparaît — le focus retombait sur <body>, et la frappe suivante
   * partait ailleurs. On note le contrôle (−, +, champ, corbeille) et la
   * position de sa ligne, et on les retrouve après le re-rendu.
   * @returns {{ index: number, control: string } | null}
   */
  #rememberFocus() {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !this.contains(active)) return null;
    const row = active.closest("tr[data-key]");
    if (!(row instanceof HTMLTableRowElement)) return null;
    const control = [".quantity-minus", ".quantity-plus", "input[data-cart-line]", ".cart-items__remove"].find(
      (sel) => active.matches(sel)
    );
    if (!control) return null;
    const rows = (this.refs.cartItemRows ?? []).filter((r) => !r.classList.contains("removing"));
    return { index: Math.max(0, rows.indexOf(row)), control };
  }
  /** @param {{ index: number, control: string } | null} memo */
  #restoreFocus(memo) {
    if (!memo) return;
    const active = document.activeElement;
    if (active && active !== document.body && this.contains(active) && active.isConnected) return;
    if (active && active !== document.body && !this.contains(active)) return;
    const rows = (this.refs.cartItemRows ?? []).filter((r) => r.isConnected && !r.classList.contains("removing"));
    const row = rows[Math.min(memo.index, rows.length - 1)];
    const target = row?.querySelector(memo.control);
    if (target instanceof HTMLElement && !(target instanceof HTMLButtonElement && target.disabled)) {
      target.focus({ preventScroll: true });
    } else {
      row?.querySelector("input[data-cart-line]")?.focus({ preventScroll: true });
    }
  }
  /*
   * Le re-rendu qui suit la réponse peut remplacer l'élément qui avait reçu
   * le focus : il retombait alors sur <body>. On le repose, sur la même ligne
   * (par sa clé) ou sur l'état vide, tant que le client n'a pas mis le focus
   * ailleurs lui-même.
   */
  #applyFocusIntent() {
    const intent = this.#focusIntent;
    if (!intent) return;
    const active = document.activeElement;
    if (active && active !== document.body && !this.contains(active)) {
      this.#focusIntent = null;
      return;
    }
    const row = intent.key ? this.refs.cartItemRows?.find((r) => r.dataset.key === intent.key && r.isConnected) : null;
    const target = row ? row.querySelector(".cart-items__remove:not([hidden])") ?? row.querySelector("input[data-cart-line]") : this.querySelector(".nappi-cart-empty__button, #cart-drawer-heading");
    if (!(target instanceof HTMLElement)) return;
    if (target.matches("h2") && !target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
    if (document.activeElement !== target) target.focus({ preventScroll: true });
  }
  /**
   * Updates the quantity. Mise en file : une requête à la fois, dans l'ordre.
   * @param {Object} config - The config.
   * @param {string} [config.key] - La clé de la ligne (item.key).
   * @param {number} [config.line] - Position de la ligne (repli si la clé manque).
   * @param {string} [config.variantId] - La variante de la ligne.
   * @param {number} [config.from] - Quantité de la ligne au moment de l'action.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @returns {Promise<void>}
   */
  updateQuantity(config) {
    const row = config.key ? this.refs.cartItemRows?.find((r) => r.dataset.key === config.key) : this.refs.cartItemRows?.[Number(config.line) - 1];
    const info = row ? this.#rowInfo(row) : null;
    const key = config.key ?? info?.key;
    if (!key) return Promise.resolve();
    this.#ensureServerItems();
    this.#inFlight++;
    this.#disableCartItems();
    const change = {
      ...config,
      key,
      variantId: config.variantId ?? info?.variantId,
      from: config.from ?? info?.quantity ?? config.quantity
    };
    const run = () => this.#resolveAndSend(change);
    this.#queue = this.#queue.then(run, run);
    return this.#queue;
  }
  /**
   * Au moment de partir, la clé existe-t-elle encore ? Sinon (lignes renommées
   * par une remise), l'écart voulu est reporté sur une ligne de même taille.
   * @param {{ key: string, variantId?: string, from: number, quantity: number, action: string }} change
   * @returns {Promise<void>}
   */
  #resolveAndSend(change) {
    const items = this.#ensureServerItems();
    if (items.has(change.key)) return this.#sendUpdate(change);
    this.#removingKeys.delete(change.key);
    const delta = change.quantity - change.from;
    const sameVariant = [...items].map(([key, item]) => ({ key, ...item })).filter((item) => item.variantId && item.variantId === change.variantId);
    if (!delta || !sameVariant.length) {
      this.#needsRefresh = true;
      if (delta) this.#failures++;
      return this.#sendUpdate({ ...change, skip: true });
    }
    const target = delta > 0 ? sameVariant[0] : sameVariant.reduce((best, info) => info.quantity > best.quantity ? info : best, sameVariant[0]);
    const quantity = Math.max(0, target.quantity + delta);
    return this.#sendUpdate({ ...change, key: (
      /** @type {string} */
      target.key
    ), from: target.quantity, quantity });
  }
  /**
   * @param {{ key: string, quantity: number, action: string, skip?: boolean }} config
   * @returns {Promise<void>}
   */
  #sendUpdate(config) {
    if (config.skip) {
      return Promise.resolve().finally(() => this.#settle(config.key));
    }
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const { key, quantity } = config;
    const { cartTotal } = this.refs;
    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    const sectionsToUpdate = /* @__PURE__ */ new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    const body = JSON.stringify({
      id: key,
      quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname
    });
    cartTotal?.shimmer();
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === "change" && quantity > 0 ? "update" : "remove",
        context: "cart",
        lines: [{ id: key, quantity }],
        promise: deferredUpdatePromise.promise
      })
    );
    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body })).then((response) => {
      return response.text();
    }).then((responseText) => {
      const parsedResponseText = JSON.parse(responseText);
      resetShimmer(this);
      if (parsedResponseText.errors || !parsedResponseText.sections) {
        this.#serverItems = null;
        this.#failures++;
        if (!parsedResponseText.errors || quantity === 0) this.#needsRefresh = true;
        this.#handleCartError(key, parsedResponseText);
        deferredUpdatePromise.reject(new Error(parsedResponseText.errors || "Cart update failed"));
        return;
      }
      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        "text/html"
      );
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
      this.#updateQuantitySelectors(parsedResponseText);
      this.#serverItems = new Map(
        (parsedResponseText.items ?? []).map((item) => [
          item.key,
          { variantId: String(item.variant_id), quantity: item.quantity }
        ])
      );
      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
        detail: {
          sections: parsedResponseText.sections,
          items: parsedResponseText.items,
          itemCount: newCartItemCount,
          source: "cart-items-component",
          didError: false
        }
      });
      const focusMemo = this.#rememberFocus();
      return Promise.resolve(
        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? "hydration" : "full"
        })
      ).then(() => {
        this.#updateCartQuantitySelectorButtonStates();
        this.#restoreFocus(focusMemo);
      });
    }).catch((error) => {
      console.error(error);
      this.#needsRefresh = true;
      this.#serverItems = null;
      this.#failedKey = key;
      this.#failures++;
      resetShimmer(this);
      deferredUpdatePromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || "Failed to update cart",
          code: "SERVICE_UNAVAILABLE"
        })
      );
    }).finally(() => {
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      return this.#settle(key);
    });
  }
  /**
   * Fin d'une requête de la file : déverrouille quand la file est vide, et
   * recale le tiroir sur le serveur si une requête a échoué entre-temps.
   * @param {string} key
   */
  #settle(key) {
    this.#removingKeys.delete(key);
    this.#inFlight--;
    if (this.#inFlight > 0) return;
    this.#enableCartItems();
    const done = this.#needsRefresh ? this.#refreshFromServer() : Promise.resolve();
    this.#needsRefresh = false;
    return done.then(() => {
      setTimeout(() => {
        this.#applyFocusIntent();
        this.#focusIntent = null;
      }, 450);
    });
  }
  /**
   * Après un échec, le tiroir peut montrer un état que le serveur n'a pas
   * (ligne retirée à l'écran, quantité non enregistrée). On le redemande au
   * serveur, et on recale la pastille du panier sur le vrai nombre d'articles.
   */
  async #refreshFromServer() {
    this.#serverItems = null;
    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? "hydration" : "full"
      });
      this.#updateCartQuantitySelectorButtonStates();
      this.#showUpdateError();
      const cart = await this.fetchCartData();
      document.querySelector("cart-icon")?.renderCartBubble?.(cart?.item_count ?? 0, false);
    } catch (error) {
      console.warn("[cart-items] Refresh after a failed update did not complete:", error);
    }
  }
  /*
   * Échec réseau : le tiroir est revenu à l'état réel, mais sans un mot le
   * client croirait son clic ignoré. Le message s'affiche sur la ligne visée
   * (emplacement d'erreur d'Horizon, role="alert").
   */
  #showUpdateError() {
    const key = this.#failedKey;
    this.#failedKey = null;
    const message = this.dataset.updateError;
    if (!key || !message) return;
    const line = (this.refs.cartItemRows ?? []).findIndex((row) => row.dataset.key === key) + 1;
    if (line < 1) return;
    const text = this.refs[`cartItemError-${line}`];
    const container = this.refs[`cartItemErrorContainer-${line}`];
    if (!(text instanceof HTMLElement) || !(container instanceof HTMLElement)) return;
    text.textContent = message;
    container.classList.remove("hidden");
  }
  /**
   * Handles the cart error.
   * @param {string} key - La clé de la ligne.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} [parsedResponseText.errors] - The errors.
   */
  #handleCartError = (key, parsedResponseText) => {
    const rows = this.refs.cartItemRows ?? [];
    const line = rows.findIndex((row) => row.dataset.key === key) + 1;
    const quantityInput = line > 0 ? rows[line - 1]?.querySelector("input[data-cart-line]") : null;
    if (quantityInput instanceof HTMLInputElement) quantityInput.value = quantityInput.defaultValue;
    const cartItemError = line > 0 ? this.refs[`cartItemError-${line}`] : null;
    const cartItemErrorContainer = line > 0 ? this.refs[`cartItemErrorContainer-${line}`] : null;
    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement && parsedResponseText.errors) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove("hidden");
    }
    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || "Cart update failed",
        code: "INVALID"
      })
    );
  };
  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;
    event.promise?.then(async ({ detail }) => {
      this.#serverItems = null;
      const sections = detail?.sections;
      const cartItemsHtml = sections?.[this.sectionId];
      const wasEmptyCartDrawer = this.isDrawer && this.querySelector("[data-cart-drawer-empty]") !== null;
      const mode = this.isDrawer ? "hydration" : "full";
      const morphOptions = {
        mode,
        injectStylesheet: wasEmptyCartDrawer
      };
      if (cartItemsHtml) {
        const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);
        if (wasEmptyCartDrawer) {
          startViewTransition(() => {
            morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }, ["fill-cart-drawer"]);
        } else {
          await morphSection(this.sectionId, cartItemsHtml, morphOptions);
        }
        if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
          for (const row of this.refs.cartItemRows ?? []) {
            if (!existingKeys.has(row.dataset.key)) {
              row.classList.add("adding");
              onAnimationEnd(row, () => row.classList.remove("adding"));
            }
          }
        }
        this.#updateCartQuantitySelectorButtonStates();
        this.#serverItems = null;
      } else {
        await sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        this.#serverItems = null;
      }
    }).catch((error) => {
      if (error?.name !== "AbortError") console.warn("[cart-items] Event promise rejected:", error);
    });
  };
  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }
  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }
  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);
      for (const selector of selectors) {
        const input = selector.querySelector("input[data-cart-quantity]");
        if (!input) continue;
        input.setAttribute("data-cart-quantity", item.quantity.toString());
        if ("updateCartQuantity" in selector && typeof selector.updateCartQuantity === "function") {
          selector.updateCartQuantity();
        }
      }
    }
  }
  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll("cart-quantity-selector-component")) {
      selector.updateButtonStates?.();
    }
  }
  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;
    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });
    return this.#pendingCartFetch;
  }
  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error("Section id missing");
    return sectionId;
  }
  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== void 0;
  }
}
if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
